<script async setup lang="ts">
import { computed, reactive, ref } from "vue";
import { formatting, useContext, useLocalization, usePhrases } from "@hantera/portal-app";
import useBackend from "@/composition/useBackend";
import type {
  AvailableStockResult,
  InventoryStockHistory,
  Sku,
  SkuStockPosition,
  StockHistoryResult,
} from "@/backend/skus";

import InventoryStockChart from "./InventoryStockChart.vue";
import { toNumber, INVENTORY_SERIES } from "./sku-format";
import { DateOnly, NavigationBar, ToggleButton } from "@hantera/design-system";
import Decimal from "decimal.js";

const backend = useBackend();
const context = useContext();
const t = useLocalization("sales.inventory");

const notFound = ref(false);

const selector = computed<{ type: "skuId" | "skuNumber"; value: string }>(() => {
  const currentView = context.currentView.value!;
  if (currentView.query.id) {
    return { type: "skuId", value: currentView.query.id };
  }
  return { type: "skuNumber", value: currentView.query.number! };
});

const sku = ref<Sku | undefined>();
const availability = ref<Record<string, AvailableStockResult>>({});
const history = ref<StockHistoryResult>({ inventories: {} });

try {
  sku.value = await backend.skus.get(selector.value);

  if (!sku.value) {
    notFound.value = true;
  } else {
    context.currentView.value!.name.value = sku.value.skuNumber ?? "Inventory";

    const [avail, hist] = await Promise.all([
      backend.skus.getAvailableStock(sku.value.skuId).catch(() => ({})),
      backend.skus.getStockHistory(sku.value.skuId).catch(() => ({ inventories: {} })),
    ]);
    availability.value = avail;
    history.value = hist;
  }
} catch (err) {
  console.error(err);
  notFound.value = true;
}

const selectedTab = ref<
  "overview" | "incoming" | "allocations" | "reservations" | "backOrders"
>("overview");


const positions = computed(() => sku.value?.stockPositions ?? []);

function incomingTotal(position: SkuStockPosition): number {
  return (position.incomingStock ?? []).reduce((sum, i) => sum + toNumber(i.quantity), 0);
}

function allocatedTotal(position: SkuStockPosition): number {
  return (position.allocations ?? []).reduce((sum, a) => sum + toNumber(a.quantity), 0);
}

function reservationTotal(position: SkuStockPosition): number {
  return (position.reservations ?? []).reduce((sum, r) => sum + toNumber(r.quantity), 0);
}

function backOrderTotal(position: SkuStockPosition): number {
  return (position.backOrders ?? []).reduce((sum, b) => sum + toNumber(b.quantity), 0);
}

const allIncoming = computed(() =>
  positions.value.flatMap((p) =>
    (p.incomingStock ?? []).map((i) => ({ inventoryKey: p.inventoryKey, ...i }))
  )
);

const allAllocations = computed(() =>
  positions.value.flatMap((p) =>
    (p.allocations ?? []).map((a) => ({ inventoryKey: p.inventoryKey, ...a }))
  )
);

const allReservations = computed(() =>
  positions.value.flatMap((p) =>
    (p.reservations ?? []).map((r) => ({ inventoryKey: p.inventoryKey, ...r }))
  )
);

const allBackOrders = computed(() =>
  positions.value.flatMap((p) =>
    (p.backOrders ?? []).map((b) => ({ inventoryKey: p.inventoryKey, ...b }))
  )
);

const hiddenByInventory = reactive<Record<string, string[]>>({});

function setVisible(inventoryKey: string, label: string, visible: boolean) {
  const hidden = hiddenByInventory[inventoryKey] ?? [];
  hiddenByInventory[inventoryKey] = visible
    ? hidden.filter((l) => l !== label)
    : [...hidden, label];
}

function historyFor(inventoryKey: string): InventoryStockHistory {
  return history.value.inventories[inventoryKey] ?? { physicalStock: [], allocations: [], reservations: [], backOrders: [] };
}

function backOrdersFor(inventoryKey: string) {
  return positions.value
    .filter((p) => p.inventoryKey === inventoryKey)
    .flatMap((p) => p.backOrders ?? []);
}

function reservationCoverage(row: {
  incomingStock?: { incomingStockId?: string; reference?: string; expectedOn?: unknown };
}): string {
  const incoming = row.incomingStock;
  if (!incoming?.incomingStockId) {
    return t("coveredByPhysical");
  }
  if (incoming.reference) return incoming.reference;
  if (incoming.expectedOn) return formatting.date(incoming.expectedOn as DateOnly);
  return "—";
}

function openOrder(orderNumber?: string): void {
  if (!orderNumber) return;
  context.openView("/order", { query: { id: orderNumber } });
}
</script>


<template>
  <template v-if="notFound">
    <div class="not-found">{{ t("notFound") }}</div>
  </template>
  <div v-else-if="sku" class="sku-view neat-scroll">
    <header class="sku-header">
      <div class="sku-title">
        <h2>{{ sku.skuNumber }}</h2>
        <span class="sku-id">{{ sku.skuId }}</span>
      </div>
      <div class="sku-meta">
        <span>{{ t("createdAt") }}: {{ formatting.dateTime(sku.createdAt as Date) }}</span>
      </div>
    </header>

    <NavigationBar
      v-model="selectedTab"
      :items="[{
        id: 'overview',
        label: t('tabs.overview')
      }, {
        id: 'incoming',
        label: t('tabs.incoming')
      }, {
        id: 'allocations',
        label: t('tabs.allocations')
      }, {
        id: 'reservations',
        label: t('tabs.reservations')
      }, {
        id: 'backOrders',
        label: t('tabs.backOrders')
      }]"

    />


    <section v-show="selectedTab === 'overview'" class="tab-content">
      <div v-if="history.truncatedBefore" class="truncation-note">
        {{ t("truncatedBefore") }}: {{ formatting.dateTime(history.truncatedBefore as Date) }}
      </div>
      <div v-if="positions.length === 0" class="empty">{{ t("noStock") }}</div>
      <div class="inventory-cards">
        <div v-for="position in positions" :key="position.stockPositionId" class="box-content inventory-card">
          <h3>{{ position.inventoryKey! }}</h3>
          <div class="inventory-card-body">
            <div class="inventory-legend">
              <ToggleButton
                :model-value="!(hiddenByInventory[position.inventoryKey!] ?? []).includes(INVENTORY_SERIES.physical.label)"
                @update:model-value="(v) => setVisible(position.inventoryKey!, INVENTORY_SERIES.physical.label, v)"
              >
                <span class="legend-dot" :style="{ background: INVENTORY_SERIES.physical.color }"></span>
                <span>{{ t("physical") }}</span>
                <span class="legend-value">{{ formatting.number(position.physicalStock as Decimal) }}</span>
              </ToggleButton>
              <ToggleButton
                :model-value="!(hiddenByInventory[position.inventoryKey!] ?? []).includes(INVENTORY_SERIES.incoming.label)"
                @update:model-value="(v) => setVisible(position.inventoryKey!, INVENTORY_SERIES.incoming.label, v)"
              >
                <span class="legend-dot" :style="{ background: INVENTORY_SERIES.incoming.color }"></span>
                <span>{{ t("incoming") }}</span>
                <span class="legend-value">{{ incomingTotal(position).toLocaleString() }}</span>
              </ToggleButton>
              <ToggleButton
                :model-value="!(hiddenByInventory[position.inventoryKey!] ?? []).includes(INVENTORY_SERIES.reservations.label)"
                @update:model-value="(v) => setVisible(position.inventoryKey!, INVENTORY_SERIES.reservations.label, v)"
              >
                <span class="legend-dot" :style="{ background: INVENTORY_SERIES.reservations.color }"></span>
                <span>{{ t("tabs.reservations") }}</span>
                <span class="legend-value">{{ reservationTotal(position).toLocaleString() }}</span>
              </ToggleButton>
              <ToggleButton
                :model-value="!(hiddenByInventory[position.inventoryKey!] ?? []).includes(INVENTORY_SERIES.allocated.label)"
                @update:model-value="(v) => setVisible(position.inventoryKey!, INVENTORY_SERIES.allocated.label, v)"
              >
                <span class="legend-dot" :style="{ background: INVENTORY_SERIES.allocated.color }"></span>
                <span>{{ t("allocated") }}</span>
                <span class="legend-value">{{ allocatedTotal(position).toLocaleString() }}</span>
              </ToggleButton>
              <ToggleButton
                :model-value="!(hiddenByInventory[position.inventoryKey!] ?? []).includes(INVENTORY_SERIES.backOrders.label)"
                @update:model-value="(v) => setVisible(position.inventoryKey!, INVENTORY_SERIES.backOrders.label, v)"
              >
                <span class="legend-dot" :style="{ background: INVENTORY_SERIES.backOrders.color }"></span>
                <span>{{ t("tabs.backOrders") }}</span>
                <span class="legend-value">{{ backOrderTotal(position).toLocaleString() }}</span>
              </ToggleButton>
            </div>
            <div class="inventory-chart">
              <InventoryStockChart
                :hidden="hiddenByInventory[position.inventoryKey!]"
                :history="historyFor(position.inventoryKey!)"
                :availability="availability[position.inventoryKey!]"
                :back-orders="backOrdersFor(position.inventoryKey!)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-show="selectedTab === 'incoming'" class="tab-content">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t("inventory") }}</th>
            <th>{{ t("reference") }}</th>
            <th>{{ t("expectedOn") }}</th>
            <th class="num">{{ t("quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in allIncoming" :key="row.incomingStockId">
            <td>{{ row.inventoryKey }}</td>
            <td>{{ row.reference }}</td>
            <td>{{ formatting.date(row.expectedOn as DateOnly) }}</td>
            <td class="num">{{ formatting.number(row.quantity as Decimal) }}</td>
          </tr>
          <tr v-if="allIncoming.length === 0">
            <td colspan="4" class="empty">{{ t("noIncoming") }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-show="selectedTab === 'allocations'" class="tab-content">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t("inventory") }}</th>
            <th>{{ t("reference") }}</th>
            <th>{{ t("from") }}</th>
            <th>{{ t("until") }}</th>
            <th class="num">{{ t("quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in allAllocations" :key="row.stockAllocationId">
            <td>{{ row.inventoryKey }}</td>
            <td>{{ row.reference }}</td>
            <td>{{ formatting.date(row.from as DateOnly) }}</td>
            <td>{{ formatting.date(row.until as DateOnly) }}</td>
            <td class="num">{{ formatting.number(row.quantity as Decimal) }}</td>
          </tr>
          <tr v-if="allAllocations.length === 0">
            <td colspan="5" class="empty">{{ t("noAllocations") }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-show="selectedTab === 'reservations'" class="tab-content">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t("inventory") }}</th>
            <th>{{ t("order") }}</th>
            <th>{{ t("allocation") }}</th>
            <th>{{ t("coveredBy") }}</th>
            <th>{{ t("inventoryDate") }}</th>
            <th class="num">{{ t("priority") }}</th>
            <th class="num">{{ t("quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in allReservations" :key="row.stockReservationId">
            <td>{{ row.inventoryKey }}</td>
            <td>
              <a
                v-if="row.order?.orderNumber"
                class="btn-silent"
                @click="openOrder(row.order?.orderNumber)"
              >{{ row.order.orderNumber }}</a>
              <span v-else>—</span>
            </td>
            <td>{{ row.allocation?.reference ?? "—" }}</td>
            <td>{{ reservationCoverage(row) }}</td>
            <td>{{ formatting.date(row.inventoryDate as DateOnly) }}</td>
            <td class="num">{{ toNumber(row.priority).toLocaleString() }}</td>
            <td class="num">{{ formatting.number(row.quantity as Decimal) }}</td>
          </tr>
          <tr v-if="allReservations.length === 0">
            <td colspan="7" class="empty">{{ t("noReservations") }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-show="selectedTab === 'backOrders'" class="tab-content">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t("inventory") }}</th>
            <th>{{ t("order") }}</th>
            <th>{{ t("allocationKeys") }}</th>
            <th>{{ t("inventoryDate") }}</th>
            <th class="num">{{ t("priority") }}</th>
            <th class="num">{{ t("quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in allBackOrders" :key="row.backOrderId">
            <td>{{ row.inventoryKey }}</td>
            <td>
              <a
                v-if="row.order?.orderNumber"
                class="order-link"
                @click="openOrder(row.order?.orderNumber)"
              >{{ row.order.orderNumber }}</a>
              <span v-else>—</span>
            </td>
            <td>{{ row.reservationRequest?.stockAllocations?.join(", ") || "—" }}</td>
            <td>{{ formatting.date(row.inventoryDate as DateOnly) }}</td>
            <td class="num">{{ toNumber(row.priority).toLocaleString() }}</td>
            <td class="num">{{ formatting.number(row.quantity as Decimal) }}</td>
          </tr>
          <tr v-if="allBackOrders.length === 0">
            <td colspan="6" class="empty">{{ t("noBackOrders") }}</td>
          </tr>
        </tbody>
      </table>
    </section>

  </div>
</template>

<style scoped>
.sku-view {
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.not-found {
  padding: 2rem;
  color: hsl(var(--color-40));
}

.sku-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sku-title {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.sku-title h2 {
  margin: 0;
}

.sku-id {
  font-size: 0.8rem;
  color: hsl(var(--color-40));
}

.sku-meta {
  font-size: 0.85rem;
  color: hsl(var(--color-40));
}

.tabs-bar {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid hsl(var(--color-70));
}

.tabs-bar button {
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  color: hsl(var(--color-30));
}

.tabs-bar button.active {
  border-bottom-color: hsl(var(--color-10));
  color: hsl(var(--color-10));
  font-weight: 600;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.inventory-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.inventory-card {
  padding: 1rem;
}

.inventory-card h3 {
  margin: 0 0 0.75rem 0;
}

.inventory-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.inventory-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
  margin-right: 0.5rem;
}

.legend-value {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  margin-left: 0.5rem;
}

.inventory-chart {
  min-width: 0;
  height: 280px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  text-align: left;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid hsl(var(--color-80));
}

.data-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.history-section {
  padding: 1rem;
  height: 320px;
}

.truncation-note {
  font-size: 0.85rem;
  color: hsl(var(--color-40));
  font-style: italic;
}

.empty {
  color: hsl(var(--color-40));
  padding: 1rem 0;
}
</style>
